/**
 * src/pages/WhereIsEverythingGoing/sections/TheLongDarkSection.tsx
 * "The long dark": interactive era strip (log scale)
 * Stelliferous era (now to ~10^14 yr), Degenerate era, Black-hole era (to ~10^100 yr+), Dark era (beyond)
 * after Adams & Laughlin 1997; clearly marks proton decay as hypothetical (not observed, speculative).
 */

import React, { useState } from "react";
import { COSMIC_ERAS } from "../futureData";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Moon, Sparkles, Orbit, Radio, AlertCircle } from "lucide-react";
export function TheLongDarkSection() {
  const [selectedEraIndex, setSelectedEraIndex] = useState(0);
  const currentEra = COSMIC_ERAS[selectedEraIndex] || COSMIC_ERAS[0];
  const eraIcons = [Sparkles, Orbit, Moon, Radio];
  return <section id="the-long-dark" className="w-full max-w-full min-w-0 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-border/30" aria-labelledby="long-dark-heading">
      <div className="max-w-[76rem] mx-auto min-w-0">
        <SectionHeading eyebrow="Chronology of Deep Time" title="The Long Dark" description="Formulated in the landmark 1997 astrophysical synthesis by Fred Adams and Gregory Laughlin, cosmic deep time unfolds across four vast eras governed by nuclear exhaustion, gravitational decay, and quantum evaporation." />

        {/* Proton Decay Rigor Warning */}
        <div className="ql-glass p-4 sm:p-5 mb-8 rounded-xl border border-primary/30 flex items-start gap-3 text-xs sm:text-sm text-muted-foreground font-sans min-w-0">
          <AlertCircle className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div className="leading-relaxed break-words">
            <strong className="text-foreground">Epistemic Precision Note:</strong> In
            the Degenerate Era, whether cold stellar remnants (white dwarfs, neutron
            stars) dissolve into radiation hinges entirely on{" "}
            <span className="text-primary font-semibold">proton decay</span>. Proton
            decay is predicted by Grand Unified Theories but has never been observed
            empirically; if protons are strictly stable, degenerate matter persists into
            astronomical eternity.
          </div>
        </div>

        {/* Interactive Logarithmic Era Strip */}
        <div className="ql-glass-strong p-6 sm:p-8 rounded-2xl border border-primary/30 mb-8 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-border/30">
            <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
              Adams &amp; Laughlin Log-Scale Timeline [2]
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              Era {selectedEraIndex + 1} of {COSMIC_ERAS.length}
            </span>
          </div>

          {/* Era Selector Segmented Tabs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-6 min-w-0">
            {COSMIC_ERAS.map((era, idx) => {
            const isSelected = idx === selectedEraIndex;
            const Icon = eraIcons[idx] || Sparkles;
            return <button key={era.id} type="button" onClick={() => setSelectedEraIndex(idx)} className={`p-4 rounded-xl text-left border transition-all flex flex-col justify-between gap-3 min-w-0 ${isSelected ? "bg-primary/20 border-primary shadow-[0_0_16px_var(--glow-primary)] ring-1 ring-primary" : "bg-card/40 border-border/50 hover:border-primary/40 hover:bg-card/60"}`} aria-pressed={isSelected}>
                  <div className="flex items-center justify-between gap-2">
                    <Icon className="size-4 text-primary shrink-0" />
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {era.rangeLog}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-serif text-sm sm:text-base font-bold text-foreground leading-snug break-words">
                      {era.name}
                    </h4>
                    <span className="text-xs text-muted-foreground break-words block font-sans mt-0.5">
                      {era.timeRange}
                    </span>
                  </div>
                  <div className="pt-1 border-t border-border/30">
                    <EvidenceBadge level={era.level} compact />
                  </div>
                </button>;
          })}
          </div>
        </div>

        {/* Selected Era Deep Exploration Panel */}
        <div className="ql-glass p-6 sm:p-8 lg:p-10 rounded-2xl border border-primary/40 flex flex-col gap-6 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-4 border-b border-border/30">
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <EvidenceBadge level={currentEra.level} />
                <span className="font-mono text-xs text-primary px-2.5 py-0.5 rounded bg-primary/10 border border-primary/20">
                  Timescale: {currentEra.rangeLog}
                </span>
                {currentEra.refs && <span className="inline-flex items-center">
                    {currentEra.refs.map(r => <Footnote key={r} n={r} />)}
                  </span>}
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight break-words">
                {currentEra.name}
              </h3>
              <p className="font-mono text-xs sm:text-sm text-muted-foreground mt-1">
                Temporal Horizon: {currentEra.timeRange}
              </p>
            </div>
          </div>

          <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-sans break-words">
            {currentEra.description}
          </p>

          <div className="space-y-3 pt-2">
            <h4 className="font-serif text-sm font-semibold text-primary uppercase tracking-wider">
              Characteristic Astrophysical Phenomena:
            </h4>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-3.5 list-none p-0 m-0 min-w-0">
              {currentEra.keyPhenomena.map((phenom, i) => <li key={i} className="p-4 rounded-xl bg-card/50 border border-border/50 text-xs sm:text-sm text-muted-foreground leading-relaxed flex items-start gap-2.5 min-w-0">
                  <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                  <span className="break-words font-sans">{phenom}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}